// MARK://dhk8/admin-shell/pages/modules/RoleDetailPage
// Ficha "ver-primero" de un ROL del negocio (Gustavo 2026-06-23). Header con
// nombre + pills (Integrado/Personalizado · Solo lectura) + KPIs (módulos /
// miembros) + Tabs (General, Módulos/Permisos, Miembros, Historial). El tab
// General se ve primero; "Editar" abre el SectionedSheet con el MISMO formulario
// (nombre + descripción + picker de módulos) que el modal de RolesPage — reusa el
// MISMO patrón de picker `.rol-mod*`, no lo duplica como otro widget.
//
// Built-ins (Integrado) son de SOLO LECTURA: el backend rechaza mutarlos, así que
// "Editar" queda deshabilitado para ellos (igual que la lista los muestra read-only).
//
// Carga por id: roles no tiene getRole(id) → se trae listRoles() y find(id). Los
// MIEMBROS (hay client) salen de users.listUsers() filtrando por u.role === rol.name.
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, ShieldCheck } from 'lucide-react'
import { MODULES, type AppKey } from '@dockethub/contracts/shared'

import * as roles from '../../lib/clients/roles.js'
import type { Role } from '../../lib/clients/roles.js'
import * as users from '../../lib/clients/users.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'

type Tab = 'general' | 'modulos' | 'miembros' | 'historial'

// Etiqueta amistosa de un rol: built-ins traen su nombre es-MX en `description`;
// si no, Title-case del `name` técnico. (Idéntico a RolesPage.roleTitle.)
function roleTitle(r: Role): string {
  if (r.builtin && r.description) return r.description
  return r.name.charAt(0).toUpperCase() + r.name.slice(1)
}

// Agrupa el catálogo igual que el editor de RolesPage: módulos del Admin y del POS,
// ordenados por etiqueta (un módulo puede estar en ambas apps).
const MODULE_GROUPS: { app: AppKey; label: string; mods: typeof MODULES[number][] }[] = [
  { app: 'admin', label: 'En el Admin', mods: MODULES.filter((m) => m.apps.includes('admin')).slice().sort((a, b) => a.label.localeCompare(b.label, 'es')) },
  { app: 'pos', label: 'En el POS', mods: MODULES.filter((m) => m.apps.includes('pos')).slice().sort((a, b) => a.label.localeCompare(b.label, 'es')) },
]

interface FormState { name: string; description: string; modules: Set<string> }

export default function RoleDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // roles.ts no expone getRole(id): traemos la lista y resolvemos por id (como
  // ProductDetailPage hace con las recetas).
  const list = useAsync((s) => roles.listRoles(s), [])
  // Miembros: hay client (users.listUsers) → se filtra por nombre técnico del rol.
  const staff = useAsync((s) => users.listUsers(undefined, s), [])

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<FormState | null>(null)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const role = useMemo(() => (list.data ?? []).find((r) => r.id === id) ?? null, [list.data, id])

  // Set de módulos activos del rol, para pintar el grid read-only del tab.
  const roleModuleSet = useMemo(() => new Set(role?.modules ?? []), [role])

  // Miembros con este rol (match por nombre técnico, p. ej. "waiter").
  const members = useMemo(
    () => (staff.data ?? []).filter((u) => role && u.role === role.name),
    [staff.data, role],
  )

  function openEdit() {
    if (!role || role.builtin) return
    setForm({ name: role.name, description: role.description ?? '', modules: new Set(role.modules) })
    setFormError(null)
    setEditing(true)
  }

  function toggleModule(key: string) {
    setForm((f) => {
      if (!f) return f
      const next = new Set(f.modules)
      if (next.has(key)) next.delete(key); else next.add(key)
      return { ...f, modules: next }
    })
  }

  async function save() {
    if (!form || !role) return
    setBusy(true); setFormError(null)
    try {
      await roles.updateRole(role.id, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        modules: [...form.modules],
      })
      toast('Rol actualizado', 'success')
      setEditing(false)
      list.reload()
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e))
    } finally { setBusy(false) }
  }

  const kpis = useMemo(() => {
    if (!role) return []
    return [
      { label: 'Módulos', value: role.modules.length },
      { label: 'Miembros', value: members.length },
    ]
  }, [role, members.length])

  const selectedCount = form?.modules.size ?? 0

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/roles')} aria-label="Volver a roles"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Rol</h1><p className="adm-page__sub">Personal · ficha del rol</p></div>
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}
      {!list.loading && !role && (
        <div className="adm-empty">No se encontró este rol. <button className="adm-btn adm-btn--sm" style={{ marginLeft: 8 }} onClick={() => navigate('/roles')}>Volver a roles</button></div>
      )}

      {role && (
        <EntityDetailPage
          title={roleTitle(role)}
          subtitle={role.description && !role.builtin ? role.description : (role.builtin ? 'Rol integrado de la plataforma' : 'Rol a la medida del negocio')}
          pills={<>
            <StatusPill tone={role.builtin ? 'info' : 'ok'} label={role.builtin ? 'Integrado' : 'Personalizado'} />
            {role.builtin && <StatusPill tone="neutral" label="Solo lectura" />}
          </>}
          kpis={<SummaryStrip items={kpis} />}
          actions={
            role.builtin
              ? <span className="adm-row__meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><ShieldCheck size={15} aria-hidden /> Rol integrado (no editable)</span>
              : <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>
          }
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'modulos', label: 'Módulos / Permisos', count: role.modules.length },
              { id: 'miembros', label: 'Miembros', count: members.length },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones del rol"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre del rol">{roleTitle(role)}</Field>
              {!role.builtin && <Field label="Nombre técnico"><span className="adm-mono">{role.name}</span></Field>}
              <Field label="Descripción">{role.description || <span className="adm-muted">Sin descripción</span>}</Field>
              <Field label="Tipo">{role.builtin ? 'Integrado (de solo lectura, sirve de plantilla)' : 'Personalizado'}</Field>
              <Field label="Módulos visibles">{role.modules.length > 0 ? `${role.modules.length} módulo${role.modules.length === 1 ? '' : 's'}` : <span className="adm-muted">Sin módulos</span>}</Field>
              <Field label="Miembros">{members.length > 0 ? `${members.length} persona${members.length === 1 ? '' : 's'}` : <span className="adm-muted">Nadie usa este rol</span>}</Field>
            </div>
          )}

          {tab === 'modulos' && (
            <div className="adm-card adm-card--pad">
              <p className="adm-row__meta" style={{ margin: '0 0 12px' }}>
                Módulos que este rol puede ver en el menú. {role.builtin ? 'Los roles integrados no se editan.' : 'Usa “Editar” para cambiar la selección.'}
              </p>
              <div className="rol-modgrid">
                {MODULE_GROUPS.map((g) => (
                  <div key={g.app} className="rol-modgrid__group">
                    <div className="rol-modgrid__grouphead">{g.label}</div>
                    {g.mods.map((m) => {
                      const on = roleModuleSet.has(m.key)
                      return (
                        <div key={m.key} className={`rol-mod rol-mod--ro${on ? ' rol-mod--on' : ' rol-mod--off'}`}>
                          <input type="checkbox" checked={on} disabled readOnly aria-label={on ? `${m.label} activado` : `${m.label} no activado`} />
                          <span className="rol-mod__text">
                            <span className="rol-mod__label">{m.label}</span>
                            <span className="rol-mod__desc">{m.description}</span>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
              <Styles />
            </div>
          )}

          {tab === 'miembros' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(staff.loading, staff.error, staff.reload)}
              <div className="adm-list">
                {!staff.loading && members.length === 0 && (
                  <div className="adm-empty">Nadie tiene este rol todavía. Asigna el rol a tu personal desde la pantalla de Personal.</div>
                )}
                {members.map((u) => (
                  <div key={u.id} className="adm-row adm-row--link" role="button" tabIndex={0}
                    onClick={() => navigate(`/users/${u.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/users/${u.id}`) } }}>
                    <div className="adm-row__main">
                      <div className="adm-row__title">{u.fullName}</div>
                      <div className="adm-row__meta">{u.email || <span className="adm-muted">Sin correo</span>}{u.pinId ? ` · ID POS ${u.pinId}` : ''}</div>
                    </div>
                    <div className="adm-row__tail">
                      <StatusPill tone={u.status === 'ACTIVE' ? 'ok' : 'danger'} label={u.status === 'ACTIVE' ? 'Activo' : 'Suspendido'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={role.id} />}
        </EntityDetailPage>
      )}

      {/* Editar rol — MISMO formulario que el modal de RolesPage (nombre + descripción
          + picker de módulos), reubicado aquí. Solo para roles personalizados. */}
      {form && role && (
        <SectionedSheet
          open={editing}
          title={`Editar rol “${roleTitle(role)}”`}
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          <FormSection title="Identidad">
            <label className="adm-field"><span className="adm-field__label">Nombre del rol</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="p. ej. Anfitrión, Supervisor de barra…" /></label>
            <label className="adm-field"><span className="adm-field__label">Descripción (opcional)</span><input className="adm-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="¿Para qué sirve este rol?" /></label>
          </FormSection>
          <FormSection title="Módulos que puede ver">
            <div className="adm-field">
              <span className="adm-field__label">Selección {selectedCount > 0 && <span className="adm-badge adm-badge--info" style={{ marginLeft: 6 }}>{selectedCount}</span>}</span>
              <p className="adm-row__meta" style={{ margin: '0 0 8px' }}>Marca lo que este rol verá en el menú. Lo que no marques quedará oculto para ese usuario.</p>
              <div className="rol-modgrid">
                {MODULE_GROUPS.map((g) => (
                  <div key={g.app} className="rol-modgrid__group">
                    <div className="rol-modgrid__grouphead">{g.label}</div>
                    {g.mods.map((m) => {
                      const checked = form.modules.has(m.key)
                      return (
                        <label key={m.key} className={`rol-mod${checked ? ' rol-mod--on' : ''}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleModule(m.key)} />
                          <span className="rol-mod__text">
                            <span className="rol-mod__label">{m.label}</span>
                            <span className="rol-mod__desc">{m.description}</span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          </FormSection>
          {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}
          <Styles />
        </SectionedSheet>
      )}

      <Styles />
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}

// Picker de módulos — MISMO patrón visual que el editor de RolesPage (.rol-mod*),
// con una variante read-only para el tab Módulos/Permisos (estado deshabilitado y
// los no-activos atenuados, para que el grid se lea como "qué ve / qué no ve").
function Styles() {
  return (
    <style>{`
  .rol-modgrid { display: flex; flex-direction: column; gap: var(--pos-space-4); }
  .rol-modgrid__group { display: flex; flex-direction: column; gap: 4px; }
  .rol-modgrid__grouphead { font-size: var(--pos-text-xxs); font-weight: var(--pos-weight-semibold); letter-spacing: var(--pos-tracking-widest); text-transform: uppercase; color: var(--pos-fg-tertiary); margin-bottom: 2px; }
  .rol-mod { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md); background: var(--pos-surface); cursor: pointer; transition: border-color var(--pos-duration-fast) var(--pos-ease-out), background var(--pos-duration-fast) var(--pos-ease-out); }
  .rol-mod:hover { background: var(--pos-elevated); }
  .rol-mod--on { border-color: var(--pos-tint); background: var(--pos-tint-fill); }
  .rol-mod input[type="checkbox"] { margin-top: 2px; flex-shrink: 0; width: 18px; height: 18px; accent-color: var(--pos-tint); cursor: pointer; }
  .rol-mod--ro { cursor: default; }
  .rol-mod--ro:hover { background: var(--pos-surface); }
  .rol-mod--ro.rol-mod--on:hover { background: var(--pos-tint-fill); }
  .rol-mod--ro input[type="checkbox"] { cursor: default; }
  .rol-mod--off { opacity: 0.55; }
  .rol-mod__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .rol-mod__label { font-size: var(--pos-text-sm); font-weight: var(--pos-weight-medium); color: var(--pos-fg); }
  .rol-mod__desc { font-size: var(--pos-text-xs); color: var(--pos-fg-tertiary); line-height: var(--pos-leading-snug, 1.35); }
  .adm-row--link { cursor: pointer; }
  .adm-row--link:hover { background: var(--pos-elevated); }
`}</style>
  )
}
